import { Options, Splide } from '@splidejs/splide'

import HttpService from '../../../../Resources/Private/TypeScript/Service/HttpService'
import { getElementChild, getElementChildren, getElements } from '../../../../Resources/Private/TypeScript/Utility/Dom'
import { IS_LOADING } from '../../../../Resources/Private/TypeScript/Utility/State'

interface EventHydraResponse {
  'hydra:member': EventItem[]
  'hydra:totalItems': number
  'hydra:view'?: {
    'hydra:next'?: string
  }
}

interface EventItem {
  detailLink: string
  endDate: null | string
  location: string
  registrationRequired: boolean
  startDate: null | string
  targetType: number
  title: string
  type: number
  uid: number
}

class EventList {
  private static readonly ITEMS_PER_PAGE = 6
  private static readonly MONTHS = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.']
  private static readonly SLIDER_BUFFER_ZONE = 3
  private static readonly TYPE_LABELS: Record<number, string> = {
    0: 'Vor Ort',
    1: 'Online',
    2: 'Hybrid',
  }
  private activeProviderType = ''
  private readonly apiUrl: string
  private currentPage = 1
  private hasMoreSliderItems = false
  private isLoading = false
  private readonly layout: number
  private readonly list: HTMLElement | null
  private readonly loadMoreButton: HTMLElement | null
  private readonly pageIds: number[]
  private readonly resultCount: HTMLElement | null
  private readonly skeleton: HTMLElement | null
  private slider: null | Splide = null
  private sliderCurrentPage = 1
  private readonly sliderElement: HTMLElement | null
  private totalItems = 0

  constructor(private readonly container: HTMLElement) {
    this.layout = parseInt(container.dataset.layout ?? '0', 10)
    this.list = getElementChild(container, 'event-list-items')
    this.loadMoreButton = getElementChild(container, 'loading-bar-button')
    this.resultCount = getElementChild(container, 'chip-filter-bar-result')
    this.skeleton = getElementChild(container, 'skeleton')
    this.sliderElement = getElementChild(container, 'event-slider')
    this.totalItems = this.list?.children.length ?? 0

    const apiUrlBase = container.dataset.apiUrl ?? ''
    this.apiUrl = apiUrlBase === '' ? '' : `${apiUrlBase.replace(/\/$/, '')}/events`
    this.pageIds = container.dataset.items
      ?.split(',')
      .map((id: string): number => parseInt(id.trim(), 10))
      .filter((id: number): boolean => !isNaN(id)) ?? []
  }

  public init(): void {
    if (this.layout === 0) {
      this.initSlider()
    }

    if (this.layout === 2) {
      this.initFilter()
    }

    this.initLoadMore()
  }

  private addSliderLazyLoad(): void {
    if (this.slider === null) {
      return
    }

    this.slider.on('moved', (newIndex: number): void => {
      if (this.slider === null || this.isLoading || !this.hasMoreSliderItems) {
        return
      }

      const totalSlides = this.slider.Components.Elements.list.childElementCount
      const perPage = this.slider.options.perPage ?? 3
      const remainingSlides = totalSlides - newIndex - perPage

      if (remainingSlides <= EventList.SLIDER_BUFFER_ZONE) {
        this.loadSliderItems()
      }
    })

    this.slider.on('arrows:updated', (_prev: HTMLButtonElement, next: HTMLButtonElement): void => {
      if (this.hasMoreSliderItems && next.disabled) {
        requestAnimationFrame((): void => {
          next.disabled = false
        })
      }
    })
  }

  private addSliderSkeletonSlides(): void {
    if (this.slider === null) {
      return
    }

    for (let i = 0; i < EventList.ITEMS_PER_PAGE; i++) {
      const skeleton = document.createElement('li')
      skeleton.className = 'skeleton__item skeleton__item--event'
      skeleton.dataset.skeletonSlide = 'true'
      skeleton.innerHTML = `
        <div class="skeleton__content skeleton__content--event">
          <div class="skeleton__title"></div>
          <div class="skeleton__info">
            <div class="skeleton__date"></div>
            <div class="skeleton__date"></div>
          </div>
          <div class="skeleton__tags">
            <div class="skeleton__tag"></div>
            <div class="skeleton__tag"></div>
          </div>
        </div>
      `
      this.slider.add(skeleton)
    }
  }

  private appendSkeletonItems(): void {
    if (this.list === null) {
      return
    }

    for (let i = 0; i < EventList.ITEMS_PER_PAGE; i++) {
      const skeleton = document.createElement('li')
      skeleton.className = 'skeleton__item skeleton__item--event'
      skeleton.dataset.skeleton = 'true'
      skeleton.innerHTML = `
        <div class="skeleton__content skeleton__content--event">
          <div class="skeleton__title"></div>
          <div class="skeleton__info">
            <div class="skeleton__date"></div>
            <div class="skeleton__date"></div>
          </div>
          <div class="skeleton__tags">
            <div class="skeleton__tag"></div>
            <div class="skeleton__tag"></div>
          </div>
        </div>
      `
      this.list.appendChild(skeleton)
    }
  }

  private buildApiUrl(page: number): string {
    const params = new URLSearchParams()

    params.set('page', page.toString())
    params.set('itemsPerPage', EventList.ITEMS_PER_PAGE.toString())

    if (this.activeProviderType !== '') {
      params.set('providerType', this.activeProviderType)
    }

    this.pageIds.forEach((pid: number): void => {
      params.append('pid[]', pid.toString())
    })

    return `${this.apiUrl}?${params.toString()}`
  }

  private createItemElement(item: EventItem): HTMLElement {
    const li = document.createElement('li')
    li.className = 'event-list__item'

    const title = document.createElement('p')
    title.className = 'event-list__title'

    if (item.detailLink) {
      const link = document.createElement('a')
      link.className = 'event-list__link'
      link.href = item.detailLink
      link.textContent = item.title

      if (item.targetType === 1) {
        link.target = '_blank'
        link.rel = 'noopener noreferrer'

        const hint = document.createElement('span')
        hint.className = 'visually-hidden'
        hint.textContent = ' (öffnet in neuem Tab)'
        link.appendChild(hint)
      }

      title.appendChild(link)
    } else {
      title.textContent = item.title
    }

    li.appendChild(title)

    const meta = document.createElement('div')
    meta.className = 'event-list__meta'

    if (item.startDate || item.endDate) {
      const dateRow = document.createElement('div')
      dateRow.className = 'event-list__meta-row'

      const dateIcon = document.createElement('span')
      dateIcon.className = 'event-list__icon event-list__icon--calendar'
      dateIcon.setAttribute('aria-hidden', 'true')
      dateRow.appendChild(dateIcon)

      const dateText = document.createElement('span')
      dateText.textContent = this.formatDateTime(item.startDate, item.endDate)
      dateRow.appendChild(dateText)

      meta.appendChild(dateRow)
    }

    if (item.location) {
      const locRow = document.createElement('div')
      locRow.className = 'event-list__meta-row'

      const locIcon = document.createElement('span')
      locIcon.className = 'event-list__icon event-list__icon--pin'
      locIcon.setAttribute('aria-hidden', 'true')
      locRow.appendChild(locIcon)

      const locText = document.createElement('span')
      locText.textContent = item.location
      locRow.appendChild(locText)

      meta.appendChild(locRow)
    }

    li.appendChild(meta)

    const tags = document.createElement('ul')
    tags.className = 'tags'

    const typeLi = document.createElement('li')
    typeLi.className = 'bg-white tag'
    typeLi.textContent = EventList.TYPE_LABELS[item.type] ?? EventList.TYPE_LABELS[0]
    tags.appendChild(typeLi)

    if (item.registrationRequired) {
      const regLi = document.createElement('li')
      regLi.className = 'bg-cherub tag'
      regLi.textContent = 'Anmeldung erforderlich'
      tags.appendChild(regLi)
    }

    li.appendChild(tags)

    return li
  }

  private formatDateTime(startDate: null | string, endDate: null | string): string {
    const pad = (n: number): string => n.toString().padStart(2, '0')
    const formatFull = (value: string): string => {
      const date = new Date(value)

      return `${date.getDate()}. ${EventList.MONTHS[date.getMonth()]}, ${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`
    }

    if (!startDate) {
      return `${formatFull(endDate ?? '')} Uhr`
    }

    let result = formatFull(startDate)

    if (endDate) {
      const end = new Date(endDate)
      result += `-${pad(end.getHours())}:${pad(end.getMinutes())}`
    }

    return `${result} Uhr`
  }

  private getSliderOptions(): Options {
    return {
      arrows: true,
      /* eslint-disable perfectionist/sort-objects -- splide requires descending breakpoint order */
      breakpoints: {
        1024: {
          perMove: 1,
          perPage: 2,
        },
        768: {
          pagination: false,
          perMove: 1,
          perPage: 2,
        },
        575: {
          focus: 'center',
          gap: '1rem',
          pagination: false,
          perMove: 1,
          perPage: 1.1,
        },
      },
      /* eslint-enable perfectionist/sort-objects */
      gap: '1.25rem',
      perMove: 1,
      perPage: 3,
      type: 'slide',
    }
  }

  private hideSkeleton(): void {
    this.container.classList.remove(IS_LOADING)
  }

  private initFilter(): void {
    const pills = getElementChildren(this.container, 'chip-filter-bar-pill')

    pills.forEach((pill: HTMLElement): void => {
      pill.addEventListener('click', (): void => {
        pills.forEach((p: HTMLElement): void => p.classList.remove('chip-filter-bar__pill--active'))
        pill.classList.add('chip-filter-bar__pill--active')

        this.activeProviderType = pill.dataset.providerType ?? ''
        this.currentPage = 1
        this.loadItems(true)
      })
    })
  }

  private initLoadMore(): void {
    if (this.loadMoreButton === null) {
      return
    }

    this.loadMoreButton.addEventListener('click', (): void => {
      if (this.isLoading) {
        return
      }

      this.currentPage++
      this.loadItems(false)
    })
  }

  private initSlider(): void {
    if (this.sliderElement === null) {
      return
    }

    this.slider = new Splide(this.sliderElement, this.getSliderOptions())
    this.slider.on('resized', (): void => this.updateControlsVisibility())
    this.slider.mount()
    this.hasMoreSliderItems = this.slider.length >= EventList.ITEMS_PER_PAGE
    this.addSliderLazyLoad()
    this.updateControlsVisibility()
  }

  private async loadItems(replaceItems: boolean): Promise<void> {
    if (this.isLoading || this.list === null || this.apiUrl === '') {
      return
    }

    this.isLoading = true
    this.loadMoreButton?.classList.add(IS_LOADING)

    if (replaceItems) {
      this.list.innerHTML = ''
      this.list.hidden = true
      this.showSkeleton()
    } else {
      this.appendSkeletonItems()
    }

    try {
      const response = await HttpService.get(this.buildApiUrl(this.currentPage)) as EventHydraResponse

      this.removeSkeletonItems()

      if (replaceItems) {
        this.list.innerHTML = ''
      }

      response['hydra:member'].forEach((item: EventItem): void => {
        const element = this.createItemElement(item)
        this.list?.appendChild(element)
      })

      this.totalItems = response['hydra:totalItems']
      this.updateResultCount(this.totalItems)
      this.updateLoadMoreVisibility()
    } catch (error) {
      console.error('Error loading events:', error)
      this.removeSkeletonItems()
    } finally {
      this.isLoading = false
      this.hideSkeleton()
      this.list.hidden = false
      this.loadMoreButton?.classList.remove(IS_LOADING)
    }
  }

  private async loadSliderItems(): Promise<void> {
    if (this.slider === null || this.isLoading || this.apiUrl === '') {
      return
    }

    this.isLoading = true
    this.sliderCurrentPage++
    this.addSliderSkeletonSlides()

    try {
      const response = await HttpService.get(this.buildApiUrl(this.sliderCurrentPage)) as EventHydraResponse

      this.removeSliderSkeletonSlides()

      response['hydra:member'].forEach((item: EventItem): void => {
        const element = this.createItemElement(item)
        element.classList.add('splide__slide')
        this.slider?.add(element)
      })

      this.hasMoreSliderItems = response['hydra:view']?.['hydra:next'] !== undefined
    } catch (error) {
      console.error('Error loading slider items:', error)
      this.removeSliderSkeletonSlides()
    } finally {
      this.isLoading = false
    }
  }

  private removeSkeletonItems(): void {
    this.list?.querySelectorAll('[data-skeleton]').forEach((el: Element): void => {
      el.remove()
    })
  }

  private removeSliderSkeletonSlides(): void {
    if (this.slider === null) {
      return
    }

    const list = this.slider.Components.Elements.list
    const skeletonSlides = list.querySelectorAll('[data-skeleton-slide="true"]')

    skeletonSlides.forEach((el: Element): void => {
      const slide = el.closest('.splide__slide') ?? el
      slide.remove()
    })

    this.slider.refresh()
  }

  private showSkeleton(): void {
    this.container.classList.add(IS_LOADING)
  }

  private updateControlsVisibility(): void {
    if (this.slider === null || this.sliderElement === null) {
      return
    }

    const controls = this.sliderElement.querySelector<HTMLElement>('.splide__controls')

    if (controls === null) {
      return
    }

    const perPage = Math.ceil(this.slider.options.perPage ?? 3)
    controls.style.display = this.slider.length <= perPage ? 'none' : ''
  }

  private updateLoadMoreVisibility(): void {
    if (this.loadMoreButton === null) {
      return
    }

    const visibleCount = this.list?.children.length ?? 0
    this.loadMoreButton.hidden = visibleCount >= this.totalItems
  }

  private updateResultCount(count: number): void {
    if (this.resultCount === null) {
      return
    }

    const label = this.resultCount.dataset.label ?? 'Veranstaltungen gefunden'
    this.resultCount.textContent = `${count} ${label}`
  }
}

const containers = getElements('event-list')

containers.forEach((container: HTMLElement): void => {
  const eventList = new EventList(container)
  eventList.init()
})
